import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { CreditCard, Loader2, CheckCircle, Plus, Minus, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditsPage = () => {
  const { user, token, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(50);
  const [loading, setLoading] = useState(false);
  const [fiscalError, setFiscalError] = useState('');

  const success = searchParams.get('success');
  const purchaseAmount = searchParams.get('amount');

  React.useEffect(() => {
    if (success) {
      refreshUser();
      // Google Ads: track credit purchase conversion with real amount + transaction ID
      const amt = parseFloat(purchaseAmount) || 0;
      if (amt > 0) {
        // Use amount + timestamp as unique transaction ID (Stripe session_id not in URL)
        const txId = `credits_${amt}_${Date.now()}`;
        import('../utils/googleAds.js').then(m => m.trackCreditPurchase(amt, txId)).catch(() => {});
      }
    }
  }, [success]);

  // Check if fiscal data is complete
  const fiscal = user?.fiscal_data || {};
  const hasFiscalData = fiscal.fiscal_name && fiscal.cif && fiscal.fiscal_address && fiscal.fiscal_city && fiscal.fiscal_postal_code && fiscal.fiscal_province;

  const buyCredits = async () => {
    if (amount < 10) return toast.error(t('Mínimo 10 créditos'));
    setFiscalError('');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('amount', amount);
      fd.append('origin_url', window.location.origin);
      const res = await axios.post(`${API}/credits/buy`, fd, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error';
      if (detail.includes('datos fiscales')) {
        setFiscalError(detail);
      } else {
        toast.error(detail);
      }
      setLoading(false);
    }
  };

  const presets = [25, 50, 100, 200, 500];

  return (
    <Layout>
      <div className="max-w-xl mx-auto" data-testid="credits-page">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">{t('CRÉDITOS')}</h1>
        <p className="text-zinc-400 mb-8">{t('1 crédito = 1€ + IVA')}</p>

        {success && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-md p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <p className="text-green-400">{t('Recarga completada correctamente')}</p>
          </div>
        )}

        <div className="bg-surface border border-white/10 rounded-md p-6 mb-6">
          <p className="text-zinc-400 text-sm">{t('Tu saldo actual')}</p>
          <p className="font-mono text-5xl font-bold text-primary mt-2">{user?.credits || 0}</p>
          <p className="text-zinc-500 text-sm mt-1">{t('créditos disponibles')}</p>
        </div>

        <div className="bg-surface border border-white/10 rounded-md p-6">
          <h2 className="font-heading text-lg text-white mb-4">{t('RECARGAR')}</h2>

          <div className="flex gap-2 mb-4 flex-wrap">
            {presets.map(p => (
              <button key={p} onClick={() => setAmount(p)}
                className={`px-4 py-2 rounded-md text-sm font-mono transition-colors ${amount === p ? 'bg-primary text-white' : 'bg-surface-highlight border border-white/10 text-zinc-400 hover:text-white'}`}>
                {p}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <Button variant="outline" size="icon" onClick={() => setAmount(Math.max(10, amount - 10))} className="border-white/10"><Minus className="w-4 h-4" /></Button>
            <Input type="number" value={amount} onChange={e => setAmount(Number(e.target.value))} min={10}
              className="bg-surface-highlight border-white/10 text-white text-center font-mono text-xl w-32" />
            <Button variant="outline" size="icon" onClick={() => setAmount(amount + 10)} className="border-white/10"><Plus className="w-4 h-4" /></Button>
            <span className="text-zinc-400">= <span className="text-white font-mono font-bold">{amount}€</span> + IVA</span>
          </div>

          {!hasFiscalData && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-4 mb-4 flex items-start gap-3" data-testid="fiscal-data-warning">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-400 text-sm font-medium">{t('Datos fiscales incompletos')}</p>
                <p className="text-amber-400/70 text-xs mt-1">{t('Para generar la factura necesitas completar tus datos fiscales.')}</p>
                <button onClick={() => navigate('/profile')} className="text-primary text-xs mt-2 hover:underline font-medium">{t('Ir a Mi Perfil')}</button>
              </div>
            </div>
          )}

          {fiscalError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-md p-4 mb-4 flex items-start gap-3" data-testid="fiscal-error">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 text-sm">{fiscalError}</p>
                <button onClick={() => navigate('/profile')} className="text-primary text-xs mt-2 hover:underline font-medium">{t('Completar datos fiscales')}</button>
              </div>
            </div>
          )}

          <Button onClick={buyCredits} disabled={loading || amount < 10} className="w-full bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider h-12" data-testid="buy-credits-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
            {t('Comprar')} {amount} {t('créditos')}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default CreditsPage;
